<template>
  <v-card class="mb-4 fullWidth" @click="" id="PaymentCard">
    <v-card-title v-if="!!getTotal" primary-title>
      <div class="headline mb-0">
        {{ $vuetify.t('$vuetify.order.payment') }}
      </div>
    </v-card-title>
    <div v-else style="height: 30px"> </div>

    <v-card-title v-if="OrderTypeSpecificCouponAppliedToCart && OrderTypeSpecificCouponAppliedToCart.length">
      {{ $vuetify.t('$vuetify.checkout.couponTypes.title') }}

      <template v-for="(orderType, index) in OrderTypeSpecificCouponAppliedToCart">
        <span v-if="index">, </span> {{ $vuetify.t(`$vuetify.checkout.couponTypes[${orderType}]`) | capatalize }}
      </template>
    </v-card-title>

    <v-btn v-if="!!getTotal && canPayOnline()"
      id="addPaymentMethod"
      color="primary"
      small
      @click="setAddPaymentMethodDialog(true)"
    >
      {{ $vuetify.t('$vuetify.account.addPaymentMethod') }}
    </v-btn>

    <template v-if="paymentOptionsAreAvailable || isBillLoading || (getTotal === 0)">
      <v-expansion-panel v-if="!!getTotal" popout :value.sync="openedPanel">
        <v-expansion-panel-content
          v-for="(item, paymentMethodIndex) in 2"
          :key="paymentMethodIndex"
          v-if="!paymentMethodIndex || requiresAdditionalMethod"
        >
          <template v-slot:header>
            <div @click="scrollIntoView(paymentMethodIndex)">
              <v-list>
                <v-list-tile class="py-2" two-line>
                  <v-list-tile-action style="min-width: 30px; margin-right: 10px">
                    <v-img v-if="getPaymentType && getPaymentType[paymentMethodIndex]"
                      :height="45"
                      :width="70"
                      :src="getMethodImage(item)"
                      :cover="getPaymentType[paymentMethodIndex] != 'offline'"
                      :contain="getPaymentType[paymentMethodIndex] == 'offline'"
                    />
                  </v-list-tile-action>

                  <v-list-tile-content>
                    <v-list-tile-title>
                      {{ paymentMethodIndex == 0 ? $vuetify.t('$vuetify.order.payment') : $vuetify.t('$vuetify.checkout.secondaryMethod') }}
                    </v-list-tile-title>

                    <v-list-tile-sub-title v-if="getPaymentType && getPaymentType[paymentMethodIndex]">
                      <template v-if="getPaymentType[paymentMethodIndex] == 'online' && !!selectedCard">
                        **** {{ selectedCard.last4 }}
                      </template>
                      <template v-else-if="getPaymentType[paymentMethodIndex] == 'membership'">
                        <!-- {{ $vuetify.t('$vuetify.checkout.credits') }}? -->
                        {{ membershipTitle }}: {{ Math.min(rewards.balance, getTotal) | currency }}
                      </template>
                      <template v-else-if="getPaymentType[paymentMethodIndex] == 'offline'">
                        {{ $vuetify.t('$vuetify.account.offline') }}
                      </template>
                      <template v-else>
                        {{ getPaymentType[paymentMethodIndex] | capatalize }}
                      </template>

                      <span v-if="paymentMethodIndex || paymentMethodIndex == 1">
                        ({{ getTotal - rewards.balance | currency }} Remaining)
                      </span>
                    </v-list-tile-sub-title>
                    <v-list-tile-sub-title v-else-if="paymentMethodIndex == 1">
                      {{ getTotal - rewards.balance | currency }} Remaining
                    </v-list-tile-sub-title>
                  </v-list-tile-content>

                  <card-error-chip :card="selectedCard" />
                </v-list-tile>
              </v-list>

              <div v-if="!paymentMethodIndex && requiresAdditionalMethod" id="insufficientFundsWarning">
                {{ $vuetify.t('$vuetify.order.requiresAdditionalMethod') }}
              </div>
            </div>
          </template>
          <v-list class="checkout-form" @click="scrollIntoView(paymentMethodIndex)">
            <!-- Membership can only be a primary method -->
            <template v-if="!paymentMethodIndex && canPayWithMembership()">
              <payment-method method="membership"
                @click="selectMembership"
                :image="storeLogo"
                :index="paymentMethodIndex"
                :title="$vuetify.t('$vuetify.checkout.credits')"
                :sub-title="`${ $vuetify.t('$vuetify.common.balance') }: ${ $currency(rewards.balance) }`"
              />

              <v-divider />
            </template>

            <template v-if="canPayWithWeChat()">
              <payment-method method="wechat"
                @click="onPaymentMethod('wechat', paymentMethodIndex)"
                :image="getSrc('/img/icons/wechat-pay-logo.png')"
                :index="paymentMethodIndex"
                :title="$vuetify.t('$vuetify.checkout.weChatPay')"
              />

              <v-divider />
            </template>

            <template v-if="canUseApplePay()">
              <payment-method method="ApplePay"
                @click="onPaymentMethod('ApplePay', paymentMethodIndex)"
                :image="getSrc(`/img/icons/apple-pay.png`)"
                :index="paymentMethodIndex"
                title="Apple Pay"
              />

              <v-divider />
            </template>

            <template v-if="canPayOnline()">
              <template v-for="(method, index) in paymentMethodList">
                <v-divider v-if="index" />

                <payment-method :method="method"
                  @click="onPaymentMethod(method, paymentMethodIndex)"
                  :image="getSrc(`/img/Dark Color/${method.brand}.png`)"
                  :index="paymentMethodIndex"
                  :title='`**** ${method.last4}`'
                  :sub-title="`${ $vuetify.t('$vuetify.account.expires') }: ${method.expired}`"
                />
              </template>
            </template>

            <!-- Offline cannot be mixed with membership -->
            <template v-if="!paymentMethodIndex && canPayOffline()">
              <v-divider />

              <payment-method method="offline"
                @click="onPaymentMethod('offline', paymentMethodIndex)"
                :image="getSrc(`/img/icons/pay.png`)"
                :index="paymentMethodIndex"
                :title="$vuetify.t('$vuetify.account.offline')"
              />
            </template>
          </v-list>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- This !(getTotal === 0) check is if the user has a 0 total(or using free coupons/items) and is using Apple devices -->
      <template v-if="getPaymentType.includes('ApplePay') && !(getTotal === 0)"">
        <v-btn v-if="!isCheckoutLoading"
          class="checkout-button"
          id="apple-pay-button-icon"
          @click="onPlaceOrder()"
          :loading="isCheckoutLoading"
        ></v-btn>
        <v-btn v-else
          class="checkout-button"
          id="apple-pay-button"
          :loading="true"
        ></v-btn>
      </template>
      <v-btn v-else
        class="checkout-button"
        color="primary"
        block
        @click="onPlaceOrder()"
        :loading="isCheckoutLoading"
      >
        {{ $vuetify.t('$vuetify.order.placeOrder') }}
      </v-btn>

      <v-checkbox
        v-if="!getRequiredFields('EMAIL')"
        v-model="emailReceipt"
        :label="$vuetify.t(`$vuetify.order[${isSelectedMethod('offline') ? 'offlineEmailReceipt' : 'emailReceipt'}]`)"
        style="margin-left: 10%"
      />
    </template>
    <div v-else-if="!canPayWithCreditCard()" v-html="$vuetify.t('$vuetify.account.addPaymentMethodPrompt')" id="noPaymentMethods">
    </div>
    <div v-else v-html="$vuetify.t('$vuetify.checkout.noPaymentMethods', getStore.name)" id="noPaymentMethods">
    </div>
  </v-card>
</template>

<script type="text/javascript" src="https://js.stripe.com/v2/"></script>
<script>
import get from 'lodash/get';

import { mapMutations, mapActions, mapGetters } from 'vuex';

import { loadStripe } from '@stripe/stripe-js';
import { matchTranslation, isWeChat, isNull } from "@/services/util.service"

import PaymentMethod from '@/components/Checkout/PaymentMethod'
import CardErrorChip from '@/components/Account/CardErrorChip.vue'

export default {
  components: {
    PaymentMethod,
    CardErrorChip
  },

  data: () => ({
    openedPanel: null
  }),

  computed: {
    selectedPayMethodId: {
      get() {
        return get(this.getCheckoutInfo, 'selectedPayMethodId', '')
      },
      set(value) {
        this.SET_CHECKOUT_INFO({
          ...this.getCheckoutInfo,
          selectedPayMethodId: value
        })
      }
    },
    emailReceipt: {
      get() {
        return get(this.getCheckoutInfo, 'emailReceipt', true)
      },
      set(value) {
        this.SET_CHECKOUT_INFO({
          ...this.getCheckoutInfo,
          emailReceipt: value
        })
      }
    },

    isCheckoutLoading: {
      set(val) { this.$parent.$parent.isCheckoutLoading = val },
      get() { return this.$parent.$parent.isCheckoutLoading }
    },

    storeLogo() {
      const relativePath = get(this.getStore, 'images.LOGO[0].url') || get(this.getStore, 'images.MAIN[0].url')

      if (relativePath)
        return `${process.env.VUE_APP_API_IMAGE}${relativePath}`
      return null
    },

    paymentMethodList() {
      return get(this.getBill, 'paymentMethodList', [])
    },

    requiresAdditionalMethod() {
      return this.isSelectedMethod('membership') && this.rewards.balance < this.getTotal
    },

    selectedCard() {
      return this.paymentMethodList.find(method => method.paymentMethodId == this.selectedPayMethodId)
    },

    storePaymentOptions() {
      const paymentTypes = get(this.getStore, 'settings.orderSettings.paymentSettings', [])
      return get(paymentTypes, 'ALL', []).concat(get(paymentTypes, this.getOrderType, []))
    },

    paymentOptionsAreAvailable() {
      if (this.isBillLoading)
        return null
      return this.canPayWithMembership() || this.canPayWithCreditCard() || this.canUseApplePay() || this.canPayWithWeChat() || this.canPayOffline()
    },

    rewards() {
      return this.getCustomerRewards || {}
    },

    emailIsRequired() {
      return (this.isLoggedIn && this.getCheckoutInfo.emailReceipt || this.getRequiredFields('EMAIL'))
    },

    membershipTitle() {
      const _customName = get(this.getStore, 'settings.membershipSettings.customMembershipName')

      if (!!_customName) {
        const _translation = matchTranslation(_customName.translations)

        if (!!_translation)
          return _translation;
      }

      return this.$vuetify.t('$vuetify.checkout.credits')
    },

    ...mapGetters([
      'isBillLoading',
      'hasAnyCouponAppliedToBill',
      'hasValidCouponAppliedToBill',
      'OrderTypeSpecificCouponAppliedToCart',
      'getCustomerRewards',
      'getBill',
      'getTotal',
      'getStore',
      'isDineIn',
      'getPaymentSupport',
      'getPaymentType',
      'getOrderType',
      'isLoading',
      'isInStore',
      'isLoggedIn',
      'doesStoreSupportWeChatPay',
      'getCheckoutInfo',
      'isStoreClosed',
      'getValidTimeSlots'
    ])
  },

  methods: {
    getSrc(src) { return `${process.env.VUE_APP_ASSET_PREFIX}${src}` },

    isOptionSupported(option) {
      let orderType = this.getPaymentSupport[ this.getOrderType ]

      if (this.getPaymentSupport[ 'ALL' ] !== undefined) {
        return !!_.includes(this.getPaymentSupport[ 'ALL' ], option)
      } else {
        return !!_.includes(orderType, option)
      }
    },

    canPayWithWeChat() {
      return isWeChat() && this.doesStoreSupportWeChatPay && this.hasCouponThatAllowsType('wechat')
    },

    canPayWithCreditCard() {
      return this.canPayOnline() && !!this.paymentMethodList.length
    },
    canPayOnline() {
      return this.isOptionSupported('ONLINE') && this.isLoggedIn && this.hasCouponThatAllowsType('online')
    },

    canPayWithMembership() {
      return !!(get(this.getStore, 'features.membershipSupported')
        && this.isOptionSupported("MEMBERSHIP")
        && this.isLoggedIn && this.rewards.balance > 0
        && this.hasCouponThatAllowsType('membership'))
    },
    isPayWithMembershipDisabled() {
      if (this.canPayWithMembership()) {
        if (this.requiresAdditionalMethod && this.getPaymentType.length == 1)
          return this.$vuetify.t('$vuetify.order.requiresAdditionalMethod')
        return this.paymentIsBlocked()
      }
      return null
    },

    canUseApplePay() {
      return !!window.ApplePaySession
              && get(this.getStore, 'settings.paymentProvider', null) != 'GLOBAL'
              && this.canPayOnline()
              && this.hasCouponThatAllowsType('applepay')
    },
    canPayOffline() {
      return this.isOptionSupported('OFFLINE')
              && get(this.getBill, 'deliveryProvider', 'SELF') == 'SELF'
              && this.hasCouponThatAllowsType('offline')
    },

    checkLocation() {
      return !this.isInStore && this.isDineIn && this.getStore.settings.owaSettings.verifyLocation === true
    },
    hasCouponAndIsValid() {
      return !this.hasAnyCouponAppliedToBill || this.hasValidCouponAppliedToBill
    },
    hasCouponThatAllowsType(orderType) {
      // To make my life easier I just toLowerCased all options
      orderType = orderType.toLowerCase()

      return !this.OrderTypeSpecificCouponAppliedToCart.length ||
              !!this.OrderTypeSpecificCouponAppliedToCart.find(ot => ot == orderType)
    },
    paymentIsBlocked() {
      // Check for errors in coupons
      if (!this.hasCouponAndIsValid())
        return this.$vuetify.t('$vuetify.cart.invalidCoupon')

      return this.checkLocation()
    },
    getRequiredFields(requirement) {
      const requiredFields = get(this.getStore, 'settings.orderSettings.requiredCustomerInfomationFields', null)
      return requiredFields && requiredFields.indexOf(requirement) !== -1 && !this.isDineIn
    },
    isPlaceOderDisabled() {
      // Skip any other validation if a payment request is in progress
      if (this.isLoading)
        return true

      const { email, firstname, lastname, phoneNumber } = this.getCheckoutInfo

      // Email is only required if they want an email receipt
      const emailIsSatisfied = !this.emailIsRequired || this.isEmailValid(email)

      // Only email is required for dine in
      let nameIsSatisfied;

      if (this.getRequiredFields('FIRST_NAME')){
        nameIsSatisfied = this.isDineIn || !isNull(firstname) && !isNull(phoneNumber)
      } else if (this.getRequiredFields('NAME')){
        nameIsSatisfied = this.isDineIn || !isNull(firstname) && !isNull(lastname) && !isNull(phoneNumber)
      } else {
        nameIsSatisfied = this.isDineIn || !isNull(phoneNumber)
      }

      this.$bus.$emit('validateRun')

      if (!emailIsSatisfied || !nameIsSatisfied) {
        // 'Email is invalid'
        if (!emailIsSatisfied) {
          this.showSnack({
            text: this.$vuetify.t('$vuetify.common.invalidEmail'),
            color: 'error'
          })
        } else {
          this.showSnack({
            text: this.$vuetify.t('$vuetify.common.fillFields'),
            color: 'error'
          })
        }

        return true
      }

      // When you change days, the time is cleared. Don't let them place the order until they select one
      if (get(this.getCheckoutInfo, 'scheduledDay', '') && !get(this.getCheckoutInfo, 'scheduledTime', '')) {
        this.showSnack({
          text: this.$vuetify.t('$vuetify.common.fillFields'),
          color: 'error'
        })

        return true
      }

      // At least one payment method must be selected
      if (!this.getPaymentType.length && !(this.getTotal === 0))
        return this.$vuetify.t('$vuetify.order.noPaymentMethodSelected')

      return this.isPayWithMembershipDisabled() || this.paymentIsBlocked()
    },

    selectMembership() {
      const prevState = this.isSelectedMethod('membership')
      this.setPaymentType('membership')

      if (this.requiresAdditionalMethod) {
        this.showSnack({
          text: this.$vuetify.t('$vuetify.order.requiresAdditionalMethod'),
          color: 'warning'
        })

        // If a credit card was already selected, set online as the automatic secondary option
        if (this.selectedPayMethodId)
          this.setPaymentType('online')

        this.openedPanel = 1
      } else if (!prevState) {
        // Only disable previously selected credit cards if membership isn't sufficient
        // and if we're not unselecting membership
        this.selectedPayMethodId = null
        this.getCheckoutInfo.paymentToken = null

        this.openedPanel = 0
        this.$nextTick(() => {
          this.openedPanel = null
        })
      }
    },
    async selectApplePay(checkoutInfo = this.getCheckoutInfo) {
      // if requiresAdditionalMethod is true, apple pay is being used as a
      // secondary payment method and should only be charged the left over amount
      let amount = this.requiresAdditionalMethod ? this.getTotal - this.rewards.balance : this.getTotal

      let _this = this
      let paymentRequest = {
        countryCode: (this.getStore.country || 'CA'),
        currencyCode: (this.getStore.country || 'CA')+'D',
        total: {
          // We need to limit the decimal to 2 places for Apple Pays sake
          amount: Math.round(amount * 100) / 100,
          label: this.getStore.name
        }
      }

      let clearLoading = () => {
        _this.isCheckoutLoading = false
        _this.$parent.$parent.confirmOrder = false
      }

      this.isCheckoutLoading = true
      await Stripe.setPublishableKey(process.env.VUE_APP_STRIPE)

      let session = Stripe.applePay.buildSession(paymentRequest,
        function(result, completion) {
          checkoutInfo.paymentToken = result.token.id

          return _this.payOrder(checkoutInfo).then(() => {
            completion(ApplePaySession.STATUS_SUCCESS)
          }).catch(() => {
            completion(ApplePaySession.STATUS_FAILURE)
          }).finally(() => {
            clearLoading()
          })
        },

        function(error) {
          clearLoading()
          completion(ApplePaySession.STATUS_FAILURE);
        }
      );

      session.oncancel = function() {
        _this.showSnack({
          text: 'Apple Pay was canceled. If this wasn\'t intentional, please try again.',
          color: 'warning'
        })

        clearLoading()
      };

      return session.begin();
    },

    onPaymentMethod(method, index = null) {
      const cardHasError = (card) => {
        if (card.cvcFailed)
          return 'cvcFailed'
        if (card.postalCodeFailed)
          return 'postalCodeFailed'
        if (card.expiredCard)
          return 'expiredCard'
        return false
      }

      // If we needed a secondary option, clear the secondary if the primary change
      // Unless the selected card has an error and cannot be used
      if (this.requiresAdditionalMethod && index === 0 && !cardHasError(method))
        this.clearSelectedMethod()

      if (method.paymentMethodId) {
        if (cardHasError(method)) {
          this.showSnack({
            text: this.$vuetify.t(`$vuetify.account.payment.status[${cardHasError(method)}]`),
            color: 'error'
          })
          return
        }

        // If the provided method includes a paymentMethodId, a credit card was chosen
        this.setPaymentType(this.selectedPayMethodId != method.paymentMethodId ? 'online' : null)
        this.selectedPayMethodId = this.selectedPayMethodId != method.paymentMethodId ? method.paymentMethodId : null
      } else {
        // Otherwise, it was wechat / google pay / offline
        this.setPaymentType(method)
        this.selectedPayMethodId = null
        this.getCheckoutInfo.paymentToken = null
      }

      // We only want to manipulate the open panel if the method was called by
      // a click event (identified by the existence of the indez)
      if (index !== null) {
        this.openedPanel = index
        this.$nextTick(() => {
          // Only close the panel if a payment method was selected
          this.openedPanel = this.getPaymentType.length ? null : index
        })
      }

      // Preload stripe key maybe helps apple pay first time issue>
      if (this.getPaymentType.includes('ApplePay'))
        Stripe.setPublishableKey(process.env.VUE_APP_STRIPE)
    },
    isSelectedMethod(method, index = null) {
      if (method.paymentMethodId) {
        // If the provided method includes a paymentMethodId, a credit card was chosen
        // If requiresAdditionalMethod is true, only show the selected card on the second method
        return this.selectedPayMethodId == method.paymentMethodId && (index !== 0 || !this.requiresAdditionalMethod)
      } else if (typeof this.getPaymentType == 'object' && this.getPaymentType.length) {
        // Otherwise, it was wechat / google pay / offline
        // If requiresAdditionalMethod is true, only membership should be shown as selected on the primary method list
        return this.getPaymentType.includes(method) && (index !== 0 || !this.requiresAdditionalMethod || method == 'membership')
      } else {
        return false
      }
    },
    clearSelectedMethod() {
      this.SET_PAYMENT_TYPE(null)
      this.selectedPayMethodId = null
      this.getCheckoutInfo.paymentToken = null
    },
    getMethodImage(item) {
      const method = get(this, `getPaymentType[${item-1}]`)

      var src = ''
      if (method == 'wechat')
        src = '/img/icons/wechat-pay-logo.png'
      else if (method == 'ApplePay')
        src = `/img/icons/apple-pay.png`
      else if (method == 'online')
        src = `/img/Dark Color/${this.selectedCard ? this.selectedCard.brand : null}.png`
      else if (method == 'membership')
        return this.storeLogo
      else if (method == 'offline')
        src = `/img/icons/pay.png`
      else
        src = ''

      return this.getSrc(src)
    },
    isEligibleMethod() {
      const method = get(this, `getPaymentType[0]`)

      // Since we remember payment methods, we want to ensure the remembered
      // payment method is still Eligible for use when they return
      if (method == 'wechat')
        return this.canPayWithWeChat()
      else if (method == 'ApplePay')
        return this.canUseApplePay()
      else if (method == 'online') {
        return this.canPayOnline()
      } else if (method == 'membership')
        return this.canPayWithMembership()
      else if (method == 'offline')
        return this.canPayOffline()
      return !!method
    },

    setInitialPaymentType() {
      this.clearSelectedMethod()

      if (this.canPayWithMembership()) {
        // Promote using points when available
        this.selectMembership()
      } else if (this.canPayOnline() && this.paymentMethodList.length) {
        // User has credit card to set
        this.onPaymentMethod(get(this.getBill, 'paymentMethodList[0]', {}))
      } else if (this.canUseApplePay()) {
        // User doesn't have credit cards, but is an IOS device
        this.onPaymentMethod('ApplePay')
      } else if (this.canPayWithWeChat()) {
        // User doesn't have credit cards, but has wechat
        this.onPaymentMethod('wechat')
      } else if (this.canPayOffline()) {
        this.onPaymentMethod('offline')
      }
    },

    scrollIntoView(index = 0) {
      const el = document.getElementsByClassName('v-expansion-panel__body')

      if (el && index in el)
        setTimeout(() => el[index].scrollIntoView(), 200)
    },

    async onPlaceOrder(payByMembership) {
      const checkAllowed = this.isPlaceOderDisabled()
      if (!!checkAllowed) {
        // The is<method>Disabled functions can return either a boolean or string
        // Some scenerios (like isLoading) dont need to display an error messsage but still shouldn't allow user to proceed
        // So if a truthy value, abort, and if a string was returned, it's an error message we should display
        if (typeof checkAllowed == 'string')
          this.showSnack({
            text: checkAllowed,
            color: 'error',
            timeout: 0
          })

        // validation failed, abort
        return
      }

      this.$validator.validate().then(valid => {
        if (valid) {
          // Open the confirmation dialog in the parent page
          this.$parent.$parent.confirmOrder = true
        } else {
          const errorMessages = this.errors.items.reduce((acc, curr) => {
            const { msg } = curr

            return [ ...acc, msg ]
          }, [])

          this.showSnack({
            text: errorMessages.join('<br />'),
            color: 'error',
            timeout: 0
          })
        }
      })
    },

    isEmailValid(email) {
      if (isNull(email))
        return false

      const re = /\S+@\S+\.\S+/;
      return re.test(email);
    },

    ...mapMutations([
      'SET_CHECKOUT_INFO',
      'SET_PAYMENT_TYPE'
    ]),
    ...mapActions([
      'fetchBillView',
      'payOrder',
      'setPaymentType',
      'setAddPaymentMethodDialog',
      'showSnack'
    ])
  },

  mounted() {
    // Listen for the fetch payment cards request to be finished that gets triggered on creation of a credit card.
    // Whenever a new one is added, we want to select that one
    this.$bus.$on('PaymentListFetched', () => {
      this.fetchBillView().then(() => {
        const lastMethod = [...this.paymentMethodList].pop()

        if (!!lastMethod)
          this.onPaymentMethod(lastMethod)
      })
    })
  },

  watch: {
    isBillLoading() {
      if (!this.isBillLoading) {
        if (!this.getPaymentType || !this.getPaymentType.length || !this.isEligibleMethod()) {
          // If no payment method is selected or the selected one is no longer eligible, select an appropriate default
          this.setInitialPaymentType()
        } else if (this.getPaymentType.length > 1 && !this.requiresAdditionalMethod) {
          // If we had a secondary method selected but it is no longer required, drop it
          this.clearSelectedMethod()
          this.SET_PAYMENT_TYPE([ 'membership' ])
        }

        if (this.isSelectedMethod('online') || this.selectedPayMethodId) {
          // If the selected card was deleted, return to the default
          if (!this.selectedCard) {
            this.setInitialPaymentType()
          }
        }

        this.$nextTick(() => {
          this.openedPanel = null
        })
      }
    },

    '$vuetify.lang.current'() {
      // Need to manually trigger an update when switching languages to update the fields name
      // Unfortunatly this doesn't seem to have a global affect so needs to
      // be included in all pages that use vee-validate
      this.$nextTick(() => {
        this.$validator.validateAll();
      })
    },

    paymentOptionsAreAvailable: {
      imediate: true,
      handler() {
        if (this.paymentOptionsAreAvailable === false) {
          const getDeviceType = () => {
            const ua = navigator.userAgent;
            if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua))
              return "tablet";
            if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua))
              return "mobile";
            return "desktop";
          };

          let str = `Date: ${new Date()}`
          str += `\norderType: ${this.getOrderType}`
          str += `\ngetDeviceType: ${getDeviceType()}`
          str += `\nWallet: ${this.rewards.balance}`

          str += '\nstorePaymentOptions: '
          this.storePaymentOptions.forEach(option => str += `${option}, `);

          if (this.OrderTypeSpecificCouponAppliedToCart.length) {
            str += '\nCouponPaymentOptions: '
            this.OrderTypeSpecificCouponAppliedToCart.forEach(item => str += `${item}, `);
          }

          if (this.getOrderType == 'DELIVERY')
            str += `\ndeliveryProvider: ${get(this.getBill, 'deliveryProvider', 'SELF')}`

          this.$api.post('/order/error', {
            error: str,
            statusCode: 1010,
            storeId: this.getStore
          })
        }
      }
    }
  }
}
</script>


<style scoped>
  #PaymentCard {
    /* border: var(--card-border) !important;
    border-radius: var(--card-border-radius); */
  }

  #PaymentCard .headline {
    font-size: var(--text-size-2) !important;
  }

  #PaymentCard,
  .v-expansion-panel,
  .v-expansion-panel--popout,
  .v-expansion-panel--popout .v-expansion-panel__container--active {
    box-shadow: none !important;
  }

  #addPaymentMethod {
    position: absolute;
    top: 20px;
    right: 5px;
  }

  #insufficientFundsWarning {
    font-size: 13px;
    color: grey;
    text-align: center;
  }

  #noPaymentMethods {
    margin: 20px 0px;
    font-size: 18px;
  }

  .v-expansion-panel .v-list__tile__title {
    font-size: var(--text-size-1) !important;
  }
  .v-expansion-panel .v-list__tile__sub-title {
    font-size: var(--text-size-sub) !important;
  }

  .checkout-button {
    margin: 30px auto 0px;
    max-width: 80%;
  }

  #apple-pay-button,
  #apple-pay-button-icon {
    display: block;
    background-color: black;
    background-size: 100% 100%;
    background-origin: content-box;
    background-repeat: no-repeat;
    color: white !important;
    width: 100%;
    height: 44px;
    padding: 10px 0;
    border-radius: 10px;
  }
  #apple-pay-button-icon {
    background-image: -webkit-named-image(apple-pay-logo-white);
  }
</style>